import { useState, useEffect } from 'react';
import { plusIcon, searchIcon } from '../../utils/images';
import { ScenarioItem } from '../ScenarioItem';
import { Scenario, DraggedItemState, ItemType } from '../../types/Scenario';
import { postScenarios, getScenarios, ScenarioItem as APIScenarioItem } from '../../services/scenarioData';
import './ScenarioList.scss';

interface ScenarioListProps {
  draggedItem: DraggedItemState | null;
}

export const ScenarioList: React.FC<ScenarioListProps> = ({ draggedItem }) => {
  const [scenarios, setScenarios] = useState<Scenario[]>([
    { id: 0, tempId: 'temp-1', conditions: [], actions: [] } // No databaseId = new scenario
  ]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  // Function to convert API scenario format to frontend format
  const convertAPIToFrontendFormat = (apiScenarios: APIScenarioItem[]): Scenario[] => {
    return apiScenarios.map((apiScenario, index) => ({
      id: apiScenario.id || (index + 1), // Use database ID if available, otherwise sequential
      databaseId: apiScenario.id, // Store original database ID
      tempId: `loaded-${apiScenario.id || (index + 1)}`,
      conditions: apiScenario.conditions.map(condition => ({
        id: parseInt(condition.id) || Date.now() + Math.random(),
        type: condition.type as ItemType,
        name: condition.name,
        value: condition.value,
        mac: condition.mac
      })),
      actions: apiScenario.actions.map(action => ({
        id: action.id,
        target_type: action.target_type as "display",
        target: action.target,
        type: action.type as 'product' | 'image',
        value: action.value
      }))
    }));
  };

  // Function to fetch scenarios from database
  const fetchScenarios = async (): Promise<void> => {
    try {
      setLoading(true);
      
      const storeId = localStorage.getItem('storeId');
      if (!storeId) {
        console.error('Store ID not found in localStorage');
        return;
      }

      const apiScenarios = await getScenarios(storeId);
      
      console.log('Fetched scenarios from database:', apiScenarios);
      
      if (apiScenarios.length > 0) {
        const frontendScenarios = convertAPIToFrontendFormat(apiScenarios);
        console.log('Converted to frontend format:', frontendScenarios);
        setScenarios(frontendScenarios);
      } else {
        // If no scenarios exist, start with one empty scenario
        setScenarios([{ id: 0, tempId: 'temp-1', conditions: [], actions: [] }]);
      }
      
    } catch (error) {
      console.error('Error fetching scenarios:', error);
      // Keep the default empty scenario if fetch fails
      setScenarios([{ id: 0, tempId: 'temp-1', conditions: [], actions: [] }]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch scenarios on component mount
  useEffect(() => {
    fetchScenarios();
  }, []);

  const addScenario = (): void => {
    // New scenarios get ID = 0 until saved to API, but unique tempId for frontend
    // No databaseId means this is a new scenario that should be created, not updated
    const tempId = `temp-${Date.now()}-${scenarios.length + 1}`;
    setScenarios([...scenarios, { 
      id: 0, 
      tempId, 
      conditions: [], 
      actions: [] 
      // databaseId is undefined for new scenarios
    }]);
  };

  const saveScenarios = async (): Promise<void> => {
    try {
      setSaving(true);
      
      // Get store ID from localStorage
      const storeId = localStorage.getItem('storeId');
      if (!storeId) {
        alert('Store ID not found. Please log in again.');
        return;
      }

      // Convert scenarios to API format
      const apiScenarios: APIScenarioItem[] = scenarios
        .filter(scenario => scenario.conditions.length > 0 || scenario.actions.length > 0) // Only save non-empty scenarios
        .map(scenario => {
          const isExisting = !!scenario.databaseId;
          console.log(`Scenario ${scenario.tempId}: ${isExisting ? 'UPDATING' : 'CREATING'} (databaseId: ${scenario.databaseId})`);
          
          return {
            ...(scenario.databaseId && { id: scenario.databaseId }), // Include database ID for existing scenarios
            conditions: scenario.conditions.map(condition => ({
              id: String(condition.id),
              type: condition.type,
              name: condition.name,
              value: condition.value,
              mac: condition.mac || ''
            })),
            actions: scenario.actions.map(action => ({
              id: String(action.id),
              target_type: "display",
              target: action.target,
              type: action.type,
              value: action.value
            })),
            ai_tags: [] // Empty for now, can be populated later
          };
        });

      // Post to API - set delete_missing to true so backend deletes scenarios not in the request
      const response = await postScenarios(storeId, apiScenarios, true);
      
      console.log('Scenarios saved successfully:', response);
      alert('Scenarios saved successfully!');
      
      // Refresh scenarios after save
      await fetchScenarios();
      
    } catch (error) {
      console.error('Error saving scenarios:', error);
      alert('Failed to save scenarios. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  console.log(scenarios);

  if (loading) {
    return (
      <div className="scenario">
        <div className="scenario__header">
          <div className="scenario__header--title-section">
            <h2 className="scenario__header--title">Scenario</h2>
            <p className="scenario__description">Loading scenarios...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="scenario">
      <div className="scenario__header">
        <div className="scenario__header--title-section">
          <h2 className="scenario__header--title">Scenario</h2>
          <p className="scenario__description">Description</p>
        </div>
        <div className="scenario__actions">
          <div className="scenario__search-container">
            <img className="scenario__search-icon" src={searchIcon} alt='Search icon'/>
            <input type="text" className="scenario__search-input" />
          </div>
          <button className="scenario__add-button" onClick={addScenario}>
            <img className="scenario__add-icon" src={plusIcon} alt='Plus icon'/> 
            <p className="scenario__add-text">Add Scenario</p>
          </button>
          <button 
            className="scenario__save-button" 
            onClick={saveScenarios}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      <div className="scenario__list">          
        {scenarios.map(scenario => (
          <ScenarioItem 
            key={scenario.tempId}
            scenario={scenario}
            draggedItem={draggedItem}
            updateScenario={(updatedScenario: Scenario | null) => {
              if (updatedScenario === null) {
                // Delete the scenario
                setScenarios(scenarios.filter(s => s.tempId !== scenario.tempId));
              } else {
                // Update the scenario
                setScenarios(scenarios.map(s => 
                  s.tempId === updatedScenario.tempId ? updatedScenario : s
                ));
              }
            }}
          />
        ))}
      </div>
    </div> 
  );
};